import type { TableId } from '@/modules/common/enums/table-id.enum.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { EntityLockApi } from '@/modules/core/api/entity-lock.api.ts';
import { useUserStore } from '@/modules/core/stores/user.store.ts';
import type { EntityLock } from '@/modules/core/types/entity-lock.type.ts';
import { ENTITY_LOCK_EXPIRATION_SECONDS, EntityLockKeeper } from '@/modules/core/utils/entity-lock-keeper.ts';

export type EntityEditLockMessages = {
  locked: string;
  lockLost: string;
  lockRenewError: string;
  editingElsewhere: string;
};

export function createGeneration() {
  let generation = 0;

  return () => {
    const current = ++generation;

    return () => current === generation;
  };
}

function isLockedByOther(lock: EntityLock) {
  return lock.isLocked && lock.userName !== useUserStore().name;
}

export class EntityEditLock {
  private readonly keeper = new EntityLockKeeper();

  private lastRelease: Promise<void> = Promise.resolve();

  private lastAcquire: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly tableId: TableId,
    private readonly messages: EntityEditLockMessages
  ) {}

  acquire<T>(id: number, isCurrent: () => boolean, load: () => Promise<T>, onLost: () => void) {
    const attempt = this.lastAcquire.then(() => this.tryAcquire(id, isCurrent, load, onLost));

    this.lastAcquire = attempt.catch(() => undefined);

    return attempt;
  }

  private async tryAcquire<T>(id: number, isCurrent: () => boolean, load: () => Promise<T>, onLost: () => void) {
    let isLocked = false;

    try {
      await this.lastRelease;

      if (!isCurrent()) {
        return null;
      }

      const lock = await EntityLockApi.get(this.tableId, id);

      if (!isCurrent()) {
        return null;
      }

      if (isLockedByOther(lock)) {
        this.notifyLocked(lock);
        return null;
      }

      await EntityLockApi.lock(this.tableId, id, ENTITY_LOCK_EXPIRATION_SECONDS);
      isLocked = true;

      const [data, isEditedInOtherTab] = await Promise.all([
        load(),
        lock.isLocked && EntityLockKeeper.isHeldInOtherTab(this.tableId, id),
      ]);

      if (!isCurrent()) {
        void this.release(id);
        return null;
      }

      if (isEditedInOtherTab) {
        notification.warning(this.messages.editingElsewhere);
      }

      this.keeper.start(this.tableId, id, {
        isLockedByOther,
        onLost: (lost) => {
          notification.warning({ key: this.messages.lockLost, params: { user: lost.userName ?? '' } });
          onLost();
        },
        onRenewError: () => notification.warning(this.messages.lockRenewError),
      });

      return { data };
    } catch (error) {
      if (isLocked) {
        void this.release(id);
      }

      throw error;
    }
  }

  async isFree(id: number) {
    const lock = await EntityLockApi.get(this.tableId, id);

    if (isLockedByOther(lock)) {
      this.notifyLocked(lock);
      return false;
    }

    return true;
  }

  async close(id: number | null, shouldRelease = true) {
    this.keeper.stop();

    if (id && shouldRelease) {
      await this.release(id);
    }
  }

  private release(id: number) {
    this.lastRelease = EntityLockApi.unlock(this.tableId, id).catch(() => undefined);

    return this.lastRelease;
  }

  private notifyLocked({ userName }: EntityLock) {
    notification.warning({ key: this.messages.locked, params: { user: userName ?? '' } });
  }
}
